import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Settings, History, FileText, BarChart3, Activity } from 'lucide-react';
import { Card } from '../../components/Card';
import { EmailSettings } from './EmailSettings';
import { EmailNotificationHistory } from './EmailNotificationHistory';
import { EmailLogs } from './EmailLogs';
import { emailService, EmailTemplateSummaryDto, EmailNotificationStatsDto } from '../../services/emailService';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAccessibleEmailTabs,
  getDefaultEmailTab,
  canViewEmailStats,
  canViewEmailTemplates,
  EmailTabId,
} from '../../utils/emailManagementAccess';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface TabItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  description: string;
}

/** Returns null rather than a placeholder number when the statistic is absent. */
export const formatCount = (value: number | null | undefined): string | null =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : null;

/**
 * The API already returns deliveryRate as a percentage - EmailController computes
 * `delivered / attempted * 100` - so it is formatted directly with no further scaling.
 */
export const formatRate = (value: number | null | undefined): string | null =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)}%` : null;

interface StatValueProps {
  loading: boolean;
  error: string | null;
  value: string | null;
  caption: string | null;
  captionClass: string;
}

/**
 * Value area of a header card. Mirrors the states the Email Templates card already uses, so
 * a statistic that cannot be loaded reads as unavailable instead of falling back to a
 * plausible-looking number (BugHerd #146).
 */
export const StatValue: React.FC<StatValueProps> = ({ loading, error, value, caption, captionClass }) => {
  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
        <p className="text-lg text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error || value === null) {
    return (
      <div>
        <p className="text-3xl font-bold text-red-500">--</p>
        <p className="text-sm text-red-500">{error ? 'Error loading' : 'Not available'}</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {caption && <p className={captionClass}>{caption}</p>}
    </div>
  );
};

export const EmailManagement: React.FC = () => {
  useDocumentTitle('Email Management | SupportDesk');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // BugHerd #158: only offer what the API will serve this role. Previously every role
  // from Agent upwards saw all three tabs and landed on the Admin-only Settings tab.
  const accessibleTabs = getAccessibleEmailTabs(user?.role);
  const showStats = canViewEmailStats(user?.role);
  const showTemplates = canViewEmailTemplates(user?.role);

  // State for email templates
  const [templates, setTemplates] = useState<EmailTemplateSummaryDto[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // Live delivery statistics for the header cards. BugHerd #146: these were the constants
  // "247", "+12% from yesterday", "98.7%" and "Excellent delivery", displayed above panels
  // reporting the real figures.
  const [stats, setStats] = useState<EmailNotificationStatsDto | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const tabs: TabItem[] = [
    {
      id: 'settings',
      label: 'Email Settings',
      icon: Settings,
      path: '/admin/email/settings',
      description: 'Configure SMTP server, templates, and security settings'
    },
    {
      id: 'history',
      label: 'Notification History',
      icon: History,
      path: '/admin/email/history',
      description: 'View sent email notifications and delivery statistics'
    },
    {
      id: 'logs',
      label: 'Email Logs',
      icon: Activity,
      path: '/admin/email/logs',
      description: 'Monitor inbound and outbound email processing with detailed logging'
    }
  ];
  
  const visibleTabs = tabs.filter((tab) => accessibleTabs.includes(tab.id as EmailTabId));

  // Determine current tab from URL. Returns null for /admin/email itself, which names no
  // tab, so the effect below can send the user to one they can actually use.
  const getCurrentTab = (): EmailTabId | null => {
    const path = location.pathname;
    if (path.includes('/history')) return 'history';
    if (path.includes('/logs')) return 'logs';
    if (path.includes('/settings')) return 'settings';
    return null;
  };

  const requestedTab = getCurrentTab();
  const currentTab = requestedTab && accessibleTabs.includes(requestedTab) ? requestedTab : null;

  // Land on the first usable tab instead of the hardcoded Settings tab, which only an
  // Admin can open (BugHerd #158). Also covers a bookmark or back-button landing on a tab
  // this role lost access to.
  useEffect(() => {
    if (!accessibleTabs.length || currentTab) {
      return;
    }
    const fallback = getDefaultEmailTab(user?.role);
    if (fallback) {
      navigate('/admin/email/' + fallback, { replace: true });
    }
  }, [currentTab, accessibleTabs.length, user?.role, navigate]);

  // Fetch email templates on component mount
  useEffect(() => {
    // A 403 we can predict is not an error to display - the card is simply not applicable
    // to this role, so it is neither fetched nor rendered (BugHerd #158).
    if (!showTemplates) {
      setTemplatesLoading(false);
      return;
    }

    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        setTemplatesError(null);
        const response = await emailService.getTemplates();
        setTemplates(response.data);
      } catch (error) {
        console.error('Failed to fetch email templates:', error);
        setTemplatesError('Failed to load email templates');
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, [showTemplates]);

  // Fetch delivery statistics from the same endpoint the Notification History panel uses,
  // so the header cannot disagree with the panel rendered directly beneath it.
  useEffect(() => {
    if (!showStats) {
      setStatsLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        setStatsError(null);
        setStats(await emailService.getNotificationStats());
      } catch (error) {
        console.error('Failed to fetch email delivery statistics:', error);
        setStatsError('Failed to load email delivery statistics');
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [showStats]);

  // Calculate active templates count
  const activeTemplatesCount = templates.filter(template => template.isActive).length;

  // Captions carry adjacent figures from the same payload rather than an invented
  // comparison; the old "+12% from yesterday" had no source and the endpoint exposes no
  // day-over-day series.
  const weekCaption = formatCount(stats?.totalSentThisWeek) === null
    ? null
    : `${formatCount(stats?.totalSentThisWeek)} this week`;
  const failedCaption = formatCount(stats?.failedToday) === null
    ? null
    : `${formatCount(stats?.failedToday)} failed today`;

  const handleTabChange = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      navigate(tab.path);
    }
  };

  const renderCurrentTab = () => {
    // No default case: falling through to Settings is what put every role on the one tab
    // only an Admin can open (BugHerd #158). While the redirect effect runs, render nothing.
    switch (currentTab) {
      case 'settings':
        return <EmailSettings />;
      case 'history':
        return <EmailNotificationHistory />;
      case 'logs':
        return <EmailLogs />;
      default:
        return null;
    }
  };

  // A role with no usable tab is not offered the navigation item, so arriving here means a
  // direct link. Say so plainly rather than rendering a page of 403 errors.
  if (!accessibleTabs.length) {
    return (
      <div className="p-6">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Management</h1>
          <p className="text-gray-600">
            Email Management is not available for your role. If you need access to email
            settings, notification history or delivery logs, ask an administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Management</h1>
            <p className="text-gray-600 mt-1">
              Configure email settings, manage templates, and monitor notifications
            </p>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      {/* Cards are rendered only for roles whose endpoint permits them, so a predictable
          403 never reaches the UI as an error state (BugHerd #158). */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {showTemplates && (
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Email Templates</p>
              {templatesLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                  <p className="text-lg text-gray-500">Loading...</p>
                </div>
              ) : templatesError ? (
                <div>
                  <p className="text-3xl font-bold text-red-500">--</p>
                  <p className="text-sm text-red-500">Error loading</p>
                </div>
              ) : (
                <div>
                  <p className="text-3xl font-bold text-gray-900">{activeTemplatesCount}</p>
                  <p className="text-sm text-gray-500">Active templates</p>
                </div>
              )}
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
        )}

        {showStats && (
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Emails</p>
              <StatValue
                loading={statsLoading}
                error={statsError}
                value={formatCount(stats?.totalSentToday)}
                caption={weekCaption}
                captionClass="text-sm text-gray-500"
              />
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        )}

        {showStats && (
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
              <StatValue
                loading={statsLoading}
                error={statsError}
                value={formatRate(stats?.deliveryRate)}
                caption={failedCaption}
                captionClass="text-sm text-gray-500"
              />
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
        )}
      </div>

      {/* Tab Navigation */}
      <Card>
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-600">
              {tabs.find(t => t.id === currentTab)?.description}
            </p>
          </div>
          
          {renderCurrentTab()}
        </div>
      </Card>
    </div>
  );
};